import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { database } from '../../Firebase';
import { ref, onValue } from 'firebase/database';
import { 
  Building2, 
  Droplets, 
  Files, 
  Users, 
  Activity,
  ArrowRight,
  Baby,
  Skull,
  HeartHandshake
} from 'lucide-react';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    properties: 0,
    births: 0,
    deaths: 0,
    marriages: 0
  });

  useEffect(() => {
    // 1. Fetch Total Properties
    const propertiesRef = ref(database, 'Grampanchayat/Gramniddhi/NewMilakhatRecords');
    const unsubProperties = onValue(propertiesRef, (snapshot) => {
      setStats(prev => ({ ...prev, properties: snapshot.size || 0 }));
    });

    // 2. Fetch Birth Registrations
    const birthRef = ref(database, 'Grampanchayat/Asthapan/Nondani/Birth Registration');
    const unsubBirth = onValue(birthRef, (snapshot) => {
      setStats(prev => ({ ...prev, births: snapshot.size || 0 }));
    });

    // 3. Fetch Death Registrations
    const deathRef = ref(database, 'Grampanchayat/Asthapan/Nondani/Death Registration');
    const unsubDeath = onValue(deathRef, (snapshot) => {
      setStats(prev => ({ ...prev, deaths: snapshot.size || 0 }));
    });

    // 4. Fetch Marriage Registrations
    const marriageRef = ref(database, 'Grampanchayat/Asthapan/Nondani/Marriage Registration');
    const unsubMarriage = onValue(marriageRef, (snapshot) => {
      setStats(prev => ({ ...prev, marriages: snapshot.size || 0 }));
    });

    return () => {
      unsubProperties();
      unsubBirth();
      unsubDeath();
      unsubMarriage();
    };
  }, []);

  const modules = [
    {
      title: 'ग्रामनिधी (Property Tax)',
      description: 'मालमत्ता कर, पावती, आणि कर आकारणी व्यवस्थापन',
      icon: <Building2 className="w-8 h-8 text-blue-600" />,
      path: '/dashboard2',
      color: 'border-blue-500',
      bg: 'bg-blue-50',
      hover: 'hover:border-blue-600'
    },
    {
      title: 'पाणीपुरवठा (Water)',
      description: 'पाणीपट्टी, कनेक्शन, आणि वसुली अहवाल',
      icon: <Droplets className="w-8 h-8 text-cyan-600" />,
      path: '/panipuravtha-dashboard',
      color: 'border-cyan-500',
      bg: 'bg-cyan-50',
      hover: 'hover:border-cyan-600'
    },
    {
      title: 'आस्थापना (Establishment)',
      description: 'कर्मचारी, दाखले, आणि प्रमाणपत्र व्यवस्थापन',
      icon: <Files className="w-8 h-8 text-purple-600" />,
      path: '/asthapan-dashboard',
      color: 'border-purple-500',
      bg: 'bg-purple-50',
      hover: 'hover:border-purple-600'
    },
    {
      title: 'नोंदणी (Registrations)',
      description: 'जन्म, मृत्यू, आणि विवाह नोंदणी',
      icon: <Users className="w-8 h-8 text-rose-600" />,
      path: '/asthapan/registrations',
      color: 'border-rose-500',
      bg: 'bg-rose-50',
      hover: 'hover:border-rose-600'
    }
  ];

  const statCards = [
    {
      title: 'एकूण मालमत्ता',
      value: stats.properties,
      icon: <Building2 className="w-6 h-6 text-white" />,
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      title: 'जन्म नोंदणी',
      value: stats.births,
      icon: <Baby className="w-6 h-6 text-white" />,
      gradient: 'from-green-500 to-emerald-600'
    },
    {
      title: 'मृत्यू नोंदणी',
      value: stats.deaths,
      icon: <Skull className="w-6 h-6 text-white" />,
      gradient: 'from-red-500 to-red-600'
    },
    {
      title: 'विवाह नोंदणी',
      value: stats.marriages,
      icon: <HeartHandshake className="w-6 h-6 text-white" />,
      gradient: 'from-pink-500 to-rose-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-8 font-poppins">
      
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">ग्रामपंचायत व्यवस्थापन प्रणाली</h1>
        <p className="text-gray-500 mt-1">आपले स्वागत आहे (Welcome back)</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        {statCards.map((stat, index) => (
          <div key={index} className={`rounded-2xl p-6 shadow-lg bg-gradient-to-r ${stat.gradient} text-white transform hover:-translate-y-1 transition-transform duration-300`}>
            <div className="flex justify-between items-start">
              <div>
                <p className="text-white/80 text-sm font-medium mb-1">{stat.title}</p>
                <h3 className="text-3xl font-bold">{stat.value}</h3>
              </div>
              <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                {stat.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modules Section */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
          <Activity className="w-5 h-5 text-blue-600" />
          मुख्य विभाग (Main Modules)
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {modules.map((module, index) => (
            <div 
              key={index}
              onClick={() => navigate(module.path)}
              className={`group bg-white rounded-xl p-6 shadow-md cursor-pointer border-l-4 ${module.color} hover:shadow-xl transition-all duration-300`}
            >
              <div className="flex items-start justify-between">
                <div className="flex gap-4">
                  <div className={`p-3 rounded-lg ${module.bg} group-hover:scale-110 transition-transform duration-300`}>
                    {module.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 group-hover:text-blue-700 transition-colors">
                      {module.title}
                    </h3>
                    <p className="text-gray-500 text-sm mt-1">
                      {module.description}
                    </p>
                  </div>
                </div>
                <div className="p-2 rounded-full bg-gray-50 group-hover:bg-blue-50 transition-colors">
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transform group-hover:translate-x-1 transition-all" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default Dashboard;